<template lang="pug">
.input-reply-for-receive-request-tab
  .mb-3

  .reply-input-content.d-flex.flex-row.align-items-stretch
    .receive-message
      .content-title 受信内容
      .register-body.mt-2(v-if="replyTarget")
        .resident-info(v-if="_residentInfo")
          FormLabel(key-name="受信日時" :value="convertDatetime(replyTarget.datetime)")
          FormLabel(key-name="年齢" :value="_residentInfo.age")
          FormLabel(key-name="性別" :value="_residentInfo.gender")
          FormLabel(key-name="介護度" :value="convertCareLevelLabel(_residentInfo.careLevel)")
          FormLabel(key-name="生活保護の有無" :value="convertAcceptsWelfare(_residentInfo.welfare)")
          LabelAdl(v-model="_residentInfo.adl")
          FormLabel(key-name="退所（退院）予定日" :value="convertDate(_residentInfo.outDatetime)")
        FormLabel(key-name="メッセージ内容（補足説明等）" :value="replyTarget.messageText")
    .reply-messages.d-flex.flex-column
      .content-title 返信履歴
      .reply-mesasge-list(ref="replyMessageList")
        span.empty-reply(v-if="!_replies") 返信はありません。
        SendRequestReplyMessage.mb-2(v-else v-for="reply in _replies" :value="reply")

      .content-title.input-reply-title 返信内容入力欄
      .input-reply.register-body(v-if="replyTarget")
        .mb-3
        FormInputSelect(v-if="replyTarget.replyAcceptance == true" v-model="acceptance" key-name="受け入れ可否" name="acceptance"
          :options="_acceptanceOptions" :validation-rules="{ required: true }" :styles="{ 'max-width': '340px' }" required)
        FormTextarea(
          v-model="messageText"
          key-name="返信内容" name="message-text" placeholder="返信内容をご記載ください。"
          :validation-rules="{ required: true, max: 1000 }" :maxlength="1000"
          :styles="{ width: '100%', maxWidth: '600px', minHeight: '160px' }" required)
        
      ValidationAlert.error-alert(v-model="showValidationAlert")
      .buttons.d-flex.flex-wrap.justify-content-end.align-items-stretch.align-content-center.mr-2
        button.btn.reply-btn.mb-2(@click="onConfirmReply") 返信

  OkCancelModal(v-model="showRequestReplyRegisterModal" title="返信しますか？" @ok="onReply")
</template>

<script lang="ts">
import { Component, Prop, Watch, Vue } from "vue-property-decorator";

import CustomValidate from "@/commons/utils/CustomValidate";

import {
  Acceptance,
  OtherOfficeAcceptanceRequestRepliesGetModel,
  OtherOfficeAcceptanceRequestRepliesPostModel,
  OtherOfficeAcceptanceRequestReply
} from "@/commons/services/OtherOfficeAcceptanceRequestReplies";
import { CareLevel, ResidentInfo } from "@/commons/services/OtherOfficeAcceptanceRequests";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import ValidationAlert from "@/pc/basics/ValidationAlertByValidator.vue";
import FormLabel from "@/pc/components/forms/FormLabel.vue";
import LabelAdl from "@/pc/components/otherOfficeRoomSchedules/LabelAdl.vue";
import FormInputSelect from "@/pc/components/forms/FormInputSelect.vue";
import FormTextarea from "@/pc/components/forms/FormTextarea.vue";

import { ReceiveRequestHistoriesTableItem } from "./ReceiveRequestHistoriesTable.vue";
import SendRequestReplyMessage from "@/pc/components/otherOfficeRoomSchedules/SendRequestReplyMessage.vue";

/**
 * ### 送信タブコンポーネント
 */
@Component({
  components: {
    OkCancelModal,
    FormLabel,
    LabelAdl,
    SendRequestReplyMessage,
    FormInputSelect,
    FormTextarea,
    ValidationAlert
  },
  $_veeValidate: { validator: "new" }
})
export default class InputReplyForReceiveRequestTab extends Vue {
  /** 返信対象のリクエスト */
  @Prop()
  replyTarget!: ReceiveRequestHistoriesTableItem;
  /** リクエストに対する返信 */
  @Prop()
  replies!: OtherOfficeAcceptanceRequestRepliesGetModel;

  /** 受け入れ可否 */
  acceptance: Acceptance | null = null;
  /** 返信内容 */
  messageText: string = "";

  /** リクエスト返信登録確認モーダル表示可否 */
  showRequestReplyRegisterModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  get _residentInfo(): ResidentInfo | undefined {
    return this.replyTarget.residentInfo;
  }

  get _replies(): Array<OtherOfficeAcceptanceRequestReply> | null {
    // 返信がない場合はnullを返す
    if (!this.replies.firstReply) {
      return null;
    }
    // 返信が1件以上あった場合、返信をまとめて返す
    else {
      return [this.replies.firstReply].concat(this.replies.followUpReplies ? this.replies.followUpReplies : []);
    }
  }

  get _acceptanceOptions(): Array<{ value: number | string; label: string }> {
    return [
      { value: Acceptance.POSSIBLE, label: "受け入れ可" },
      { value: Acceptance.NEED_CONSULTATION, label: "詳細相談希望" },
      { value: Acceptance.NOT_POSSIBLE, label: "受け入れ不可" }
    ];
  }

  /**
   * vue lifecycle created
   */
  created() {
    CustomValidate.addCustomValidate(this.$validator);
    // 初期値として最新の回答状況を設定する
    if (this.replies && this.replies.latestAcceptance) {
      this.acceptance = this.replies.latestAcceptance;
    }
    return;
  }

  /**
   * vue lifecycle mounted
   */
  mounted() {
    // タブ切り替えのアニメーション等で描画が完了していない場合があるため、少し待機してからスクロールする
    setTimeout(() => {
      this.scrollToBottom();
    }, 200);
    return;
  }

  @Watch("replies")
  onRepliesChanged() {
    // データが差し替わった場合に初期値を再設定する
    if (this.replies && this.replies.latestAcceptance) {
      this.acceptance = this.replies.latestAcceptance;
    } else {
      this.acceptance = null;
    }
    this.$nextTick(() => {
      this.scrollToBottom();
    });
  }

  /**
   * ### メッセージエリアを最下部にスクロールする
   */
  private scrollToBottom(): void {
    const element = this.$refs.replyMessageList as HTMLElement;
    if (element) {
      element.scrollTo({
        top: element.scrollHeight, // スクロール位置、末尾を指定して移動させる
        behavior: "smooth" // smoothを使用して、スクロールを滑らかに移動させる
      });
    }
    return;
  }

  convertDatetime(outDatetime: number | null): string {
    if (!outDatetime) {
      return "";
    }
    return this.$dateUtil.convertNumberToString(outDatetime, "yyyy/mm/dd hh:MM");
  }

  convertCareLevelLabel(careLevel: CareLevel | null): string {
    if (!careLevel) {
      return "";
    }
    switch (careLevel) {
      case CareLevel.CARE_LEVEL_1000: {
        return "自立";
      }
      case CareLevel.CARE_LEVEL_1010: {
        return "申請中";
      }
      case CareLevel.CARE_LEVEL_2010: {
        return "要支援１";
      }
      case CareLevel.CARE_LEVEL_2020: {
        return "要支援２";
      }
      case CareLevel.CARE_LEVEL_3010: {
        return "要介護１";
      }
      case CareLevel.CARE_LEVEL_3020: {
        return "要介護２";
      }
      case CareLevel.CARE_LEVEL_3030: {
        return "要介護３";
      }
      case CareLevel.CARE_LEVEL_3040: {
        return "要介護４";
      }
      case CareLevel.CARE_LEVEL_3050: {
        return "要介護５";
      }
      default: {
        return "";
      }
    }
  }

  convertAcceptsWelfare(welfare: boolean | null): string {
    if (welfare == null) {
      return "";
    }
    if (welfare == true) {
      return "有り";
    } else {
      return "無し";
    }
  }

  convertDate(outDatetime: number | null): string {
    if (!outDatetime) {
      return "";
    }
    return this.$dateUtil.convertNumberToString(outDatetime, "yyyy/mm/dd");
  }

  /** ### 送信ボタン押下処理 */
  async onConfirmReply(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    if (validationResult) {
      this.showValidationAlert = false;
      this.showRequestReplyRegisterModal = true;
    } else {
      this.showValidationAlert = true;
    }

    return;
  }

  onReply(): void {
    const requestBody: OtherOfficeAcceptanceRequestRepliesPostModel = {
      parentKey: `${this.replyTarget.facilityId}_${this.replyTarget.datetime}`,
      acceptance: this.acceptance,
      messageText: this.messageText
    };
    this.$emit("sendReply", requestBody);
    return;
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";

.input-reply-for-receive-request-tab {
  @mixin title() {
    background-color: $bg-header;
    padding: 5px;
    min-height: 32px;
    font-weight: bold;
    text-align: center;
  }

  .reply-input-content {
    border: solid 1px $border-darkgray; // 外枠

    // 共通のコンテンツタイトル
    .content-title {
      @include title();
      border-bottom: solid 1px $border-lightgray;
      min-height: 35px;
    }

    // 左側 受信したリクエスト内容
    .receive-message {
      min-width: 50%;
      max-width: 50%;
    }
    // 右側 返信内容
    .reply-messages {
      min-width: 50%;
      max-width: 50%;
      border-left: solid 1px $border-darkgray; // 中央境界線

      // 返信履歴
      .reply-mesasge-list {
        max-height: 380px;
        overflow-y: auto;

        background-color: $bg-message;
        padding: 1rem;

        .empty-reply {
          font-weight: bold;
          text-align: center;
        }
      }

      // 返信内容入力欄
      .input-reply-title {
        border-top: solid 1px $border-darkgray; // タイトルの上線
      }
      .input-reply {
        .error-alert {
          margin: 0 8px 0 20px;
          width: calc(100% - 28px);
        }
      }
    }
  }
}
</style>
